// shared.js — reusable styled components used across page components
// Import only what you need: import { PageWrapper, Container, PageTitle } from '../styles/shared';

import styled from 'styled-components';
import { colors, fonts, spacing, radius, shadows, transitions, breakpoints } from './theme';

// ─── Page layout ────────────────────────────────────────────────────────────

export const PageWrapper = styled.main`
  min-height: 100vh;
  background: ${colors.bgGradient};
  position: relative;
  overflow: hidden;
`;

export const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${spacing.pageTopDesktop} ${spacing.pageSideDesktop} ${spacing.xxxl};
  position: relative;
  z-index: 1;

  @media (max-width: ${breakpoints.mobile}) {
    padding: ${spacing.pageTopMobile} ${spacing.pageSideMobile} ${spacing.xl};
  }
`;

export const NarrowContainer = styled(Container)`
  max-width: 860px;
`;

// Light-background pages (About, FAQ, Contact, Privacy)
export const LightPageWrapper = styled.main`
  min-height: 100vh;
  background: #fff;
  color: #111;
`;

export const LightContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${spacing.pageTopDesktop} ${spacing.pageSideDesktop} ${spacing.xxxl};
  position: relative;
  z-index: 1;

  @media (max-width: ${breakpoints.mobile}) {
    padding: ${spacing.pageTopMobile} ${spacing.pageSideMobile} ${spacing.xl};
  }
`;

// ─── Page header / titles ────────────────────────────────────────────────────

export const PageHeader = styled.header`
  text-align: center;
  margin-bottom: 50px;
  color: white;

  @media (max-width: ${breakpoints.mobile}) {
    margin-bottom: 32px;
  }
`;

export const PageTitle = styled.h1`
  font-size: ${fonts.h1};
  font-weight: ${fonts.bold};
  margin-bottom: 20px;
  letter-spacing: -1px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: white;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: ${fonts.h1Mobile};
    gap: 10px;
  }
`;

export const PageSubtitle = styled.p`
  font-size: ${fonts.bodyLg};
  color: ${colors.textWhiteMuted};
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  font-weight: ${fonts.normal};

  @media (max-width: ${breakpoints.mobile}) {
    font-size: ${fonts.body};
  }
`;

export const SectionTitle = styled.h2`
  font-size: ${fonts.h2};
  font-weight: ${fonts.bold};
  color: white;
  margin-bottom: ${spacing.md};
  letter-spacing: -0.5px;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: ${fonts.h2Mobile};
  }
`;

// ─── Breadcrumbs ─────────────────────────────────────────────────────────────

export const Breadcrumbs = styled.nav`
  padding: 12px 0;
  font-size: ${fonts.bodySm};
  color: ${colors.textWhiteMuted};

  ol {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: ${spacing.sm};
    justify-content: center;
    flex-wrap: wrap;
  }

  li {
    display: flex;
    align-items: center;
    gap: ${spacing.sm};

    &:not(:last-child)::after {
      content: '›';
      color: ${colors.textWhiteFaint};
      margin-left: ${spacing.sm};
    }
  }

  a {
    color: ${colors.textWhite};
    text-decoration: none;
    transition: ${transitions.fast};

    &:hover {
      color: white;
      text-decoration: underline;
    }
  }

  span {
    color: white;
    font-weight: ${fonts.medium};
  }
`;

// ─── Stats bar ────────────────────────────────────────────────────────────────

export const StatsBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-top: 30px;
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.mobile}) {
    gap: 20px;
  }
`;

export const StatItem = styled.div`
  text-align: center;

  .value {
    font-size: 2em;
    font-weight: ${fonts.bold};
    color: white;
    margin-bottom: 5px;
    display: block;
  }

  .label {
    font-size: ${fonts.bodySm};
    color: ${colors.textWhiteMuted};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  @media (max-width: ${breakpoints.mobile}) {
    .value { font-size: 1.5em; }
  }
`;

// ─── Cards / surfaces ─────────────────────────────────────────────────────────

export const Card = styled.div`
  background: ${colors.surface};
  border-radius: ${radius.xl};
  padding: ${spacing.xl};
  box-shadow: ${shadows.card};
  border: 1px solid ${colors.borderLight};
  transition: ${transitions.slow};

  &:hover {
    box-shadow: ${shadows.cardHover};
    transform: translateY(-2px);
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: ${spacing.lg};
    border-radius: ${radius.lg};
  }
`;

export const GlassPanel = styled.div`
  background: ${colors.glassBg};
  border: 1px solid ${colors.glassBorder};
  border-radius: ${radius.xl};
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
`;

// ─── Buttons ─────────────────────────────────────────────────────────────────

export const PrimaryButton = styled.button`
  background: ${colors.primaryGradient};
  color: white;
  border: none;
  padding: 12px 28px;
  border-radius: ${radius.lg};
  font-size: ${fonts.body};
  font-weight: ${fonts.semibold};
  cursor: pointer;
  transition: ${transitions.base};
  display: inline-flex;
  align-items: center;
  gap: ${spacing.sm};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${shadows.primaryGlow};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
    justify-content: center;
    padding: 14px 16px;
    font-size: 16px;
  }
`;

export const SecondaryButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 12px 28px;
  border-radius: ${radius.lg};
  font-size: ${fonts.body};
  font-weight: ${fonts.semibold};
  cursor: pointer;
  transition: ${transitions.base};
  display: inline-flex;
  align-items: center;
  gap: ${spacing.sm};

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
    justify-content: center;
    padding: 14px 16px;
    font-size: 16px;
  }
`;

// ─── Table primitives (used by RegionTable, ConstituencyTable, InternationalTable) ──

export const TableContainer = styled.div`
  background: ${colors.surface};
  border-radius: ${radius.xxl};
  padding: ${spacing.xl};
  box-shadow: ${shadows.card};

  @media (max-width: ${breakpoints.mobile}) {
    padding: ${spacing.md};
    border-radius: ${radius.lg};
  }
`;

export const TableHeaderRow = styled.div`
  display: flex;
  padding: 12px 16px;
  background: ${colors.surfaceAlt};
  border-bottom: 1px solid ${colors.borderLight};
  border-radius: ${radius.md} ${radius.md} 0 0;
  font-size: ${fonts.bodySm};
  font-weight: ${fonts.semibold};
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const TableRow = styled.div`
  display: flex;
  padding: 10px 16px;
  border-bottom: 1px solid ${colors.borderLighter};
  transition: background ${transitions.fast};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${colors.surfaceAlt};
  }
`;

// ─── Form inputs (light surface, used in search panels) ──────────────────────

export const SearchInput = styled.input`
  background: ${colors.glassHover};
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: ${radius.lg};
  padding: 10px 14px 10px 38px;
  font-size: ${fonts.bodySm};
  color: white;
  transition: ${transitions.base};
  outline: none;
  width: 100%;

  &::placeholder {
    color: ${colors.textWhiteFaint};
  }

  &:focus {
    border-color: rgba(102, 126, 234, 0.65);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.14);
    background: rgba(255, 255, 255, 0.12);
  }

  /* Prevent iOS zoom — never go below 16px on mobile */
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 16px;
  }
`;

// ─── Empty / error state ──────────────────────────────────────────────────────

export const EmptyState = styled.div`
  text-align: center;
  padding: ${spacing.xxxl} ${spacing.xl};
  color: ${colors.textWhiteMuted};

  .icon {
    font-size: 3em;
    margin-bottom: ${spacing.md};
    opacity: 0.5;
  }

  h3 {
    font-size: ${fonts.h3};
    color: white;
    margin-bottom: ${spacing.sm};
  }

  p {
    font-size: ${fonts.body};
    line-height: 1.6;
  }
`;

// ─── Flex / layout helpers ────────────────────────────────────────────────────

export const Row = styled.div`
  display: flex;
  align-items: ${({ align }) => align || 'center'};
  justify-content: ${({ justify }) => justify || 'flex-start'};
  gap: ${({ gap }) => gap || spacing.md};
  flex-wrap: ${({ wrap }) => wrap || 'nowrap'};

  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: ${({ mobileColumn }) => (mobileColumn ? 'column' : 'row')};
  }
`;
